import type { CommendaAiConversation } from '@commenda/api/commenda-ai/api';
import type {
  CommendaAiConversationAccessView,
  CommendaAiConversationVisibility,
} from '@commenda/api/commenda-ai/types';
import { Icons } from '@commenda/kakashi';

export interface ConversationVisibilityOption {
  value: CommendaAiConversationVisibility;
  icon: typeof Icons.Lock01;
  badgeLabel: string;
  getTitle: (workspaceName: string) => string;
  description: string;
}

/** Resolves who can access a conversation into what the header shows the current viewer. */
export class ConversationAccessHelper {
  static readonly VISIBILITY_OPTIONS: readonly ConversationVisibilityOption[] =
    [
      {
        value: 'PRIVATE',
        icon: Icons.Lock01,
        badgeLabel: 'Private',
        getTitle: () => 'Only me',
        description: 'Only you can view and continue this chat.',
      },
      {
        value: 'WORKSPACE',
        icon: Icons.Users01,
        badgeLabel: 'Shared',
        getTitle: (workspaceName) => `Everyone in ${workspaceName}`,
        description:
          'Anyone in your workspace can view and continue this chat.',
      },
    ];

  static readonly READ_ONLY_REASON_SCHEDULED_AGENT =
    "Access follows the agent's settings.";

  static readonly READ_ONLY_REASON_NOT_OWNER =
    'Only the person who started this chat can change access.';

  static readonly READ_ONLY_REASON_PANEL = 'Open full screen to change access.';

  static getOption(
    visibility: CommendaAiConversationVisibility
  ): ConversationVisibilityOption {
    return (
      ConversationAccessHelper.VISIBILITY_OPTIONS.find(
        (option) => option.value === visibility
      ) ?? ConversationAccessHelper.VISIBILITY_OPTIONS[0]
    );
  }

  /** Sharing exists only for a conversation that belongs to a company. The backend refuses it for any
   * other (builder, research, staff threads), so for those there is no control to show at all. */
  static isShareable(
    conversation: Readonly<Pick<CommendaAiConversation, 'companyId'>>
  ): boolean {
    return (
      conversation.companyId !== null && conversation.companyId !== undefined
    );
  }

  /** Only the owner may change access, and only from the full page; a scheduled run's access
   * follows its agent, so it is never changed from the conversation. */
  static canChangeVisibility(
    conversation: Readonly<
      Pick<CommendaAiConversation, 'userId' | 'scheduledAgentId'>
    >,
    viewerUserId: string | undefined,
    layout: 'page' | 'panel'
  ): boolean {
    return (
      layout === 'page' &&
      conversation.userId === viewerUserId &&
      !conversation.scheduledAgentId
    );
  }

  static getAccessView(
    conversation: Readonly<
      Pick<
        CommendaAiConversation,
        'userId' | 'scheduledAgentId' | 'visibility' | 'createdByName'
      >
    >,
    viewerUserId: string | undefined,
    layout: 'page' | 'panel'
  ): CommendaAiConversationAccessView {
    const isOwner = conversation.userId === viewerUserId;
    return {
      visibility: conversation.visibility,
      sharedByName: isOwner ? undefined : conversation.createdByName,
      readOnlyReason: ConversationAccessHelper.getReadOnlyReason(
        conversation,
        isOwner,
        layout
      ),
    };
  }

  static getPillLabel(
    access: Readonly<CommendaAiConversationAccessView>
  ): string {
    return access.visibility === 'WORKSPACE' && access.sharedByName
      ? `Shared by ${access.sharedByName}`
      : ConversationAccessHelper.getOption(access.visibility).badgeLabel;
  }

  private static getReadOnlyReason(
    conversation: Readonly<Pick<CommendaAiConversation, 'scheduledAgentId'>>,
    isOwner: boolean,
    layout: 'page' | 'panel'
  ): string | undefined {
    if (conversation.scheduledAgentId)
      return ConversationAccessHelper.READ_ONLY_REASON_SCHEDULED_AGENT;
    if (!isOwner) return ConversationAccessHelper.READ_ONLY_REASON_NOT_OWNER;
    if (layout === 'panel')
      return ConversationAccessHelper.READ_ONLY_REASON_PANEL;
    return undefined;
  }
}
